import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight, PlayCircle, Sparkles } from "lucide-react";
import Counter from "@/components/common/Counter";
import { HERO_STATS } from "@/data/site";
import WhatsAppFloatingButton from "@/components/WhatsAppFloatingButton";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBg = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const yFloat = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);


  const phoneNumber = "971562973825";

const defaultMessage =
  "Hello PRINT ZONE! I'd like to get a custom quote for a printing project. Could you please help me with the details and pricing?";
const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(defaultMessage)}`; 
  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative min-h-[100svh] overflow-hidden bg-[#0F172A] text-white">
      {/* Background with subtle gradient */}
      <div className="absolute inset-0 bg-[#0F172A]">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1E293B] to-[#0F172A]" />
      </div>

      {/* Decorative blobs */}
      <div className="pz-blob bg-[#2563EB] w-[460px] h-[460px] -top-32 -right-32" />
      <div className="pz-blob bg-[#F59E0B] w-[360px] h-[360px] bottom-[-160px] left-[-120px] opacity-30" />

      {/* Grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.07] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.5) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
        }}
      />

      {/* Floating cards */}
      

      <motion.div style={{ y: yFloat }} className="absolute right-[10%] bottom-[18%] hidden lg:block">
        <div className="pz-glass-dark rounded-2xl p-5 w-[210px] pz-floating" style={{ animationDelay: "1.2s" }}>
          <div className="text-white/70 text-xs uppercase tracking-widest">Express Lane</div>
          <div className="mt-1 font-display text-3xl font-bold text-[#F59E0B]">48h</div>
          <div className="text-white/80 text-sm">Same-week delivery across all 7 Emirates</div>
        </div>
      </motion.div>

      {/* Content */}
      <div className="relative pz-container min-h-[100svh] flex flex-col justify-center pt-32 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 bg-white/5 text-xs uppercase tracking-[0.22em] text-white/85 backdrop-blur">
            <span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B] animate-pulse" />
            UAE-Wide · Branding · Print · Events · Gifting
          </div>

          <h1 className="mt-7 font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight">
            Advertising ,
            <span className="relative inline-block">
              <span className="relative z-10">Branding </span>
              <span className="absolute left-0 right-0 bottom-1 h-3 bg-[#F59E0B]/40 -z-0 rounded-sm" />
            </span>
            <br />&amp; Printing<br />Solutions.
          </h1>

          <p className="mt-7 text-lg sm:text-xl text-white/80 max-w-2xl leading-relaxed">
            Delivering professional printing, signage, exhibition, promotional and branding solutions for businesses across the UAE and worldwide.
          </p>

          <div className="mt-10 flex flex-wrap gap-4">
            <a
              href={whatsappUrl}
              data-testid="hero-cta-quote"
              className="inline-flex items-center gap-2 px-7 py-4 rounded-full pz-btn-gold text-base"
            >
              Get Quote <ArrowRight className="w-5 h-5" strokeWidth={2} />
            </a>
            <a
              href="tel:+971-562973825"
              data-testid="hero-cta-call"
              className="inline-flex items-center gap-2 px-7 py-4 rounded-full pz-btn-outline text-base"
            >
              <PlayCircle className="w-5 h-5" strokeWidth={1.5} /> Call Now
            </a>
          </div>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-16 lg:mt-24 grid grid-cols-3 max-w-3xl gap-2 sm:gap-6"
        >
          {HERO_STATS.map((s, i) => (
            <div key={s.label} data-testid={`hero-stat-${i}`} className="border-l border-white/15 pl-4 sm:pl-6">
              <div className="font-display text-3xl sm:text-5xl font-bold text-white">
                <Counter to={s.value} />{s.suffix}
              </div>
              <div className="mt-2 text-[11px] sm:text-sm uppercase tracking-[0.18em] text-white/65">
                {s.label}
              </div>
            </div>
          ))}
        </motion.div>
      </div>

      {/* Bottom scroll cue */}
      {/* <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/50 text-xs uppercase tracking-[0.3em] hidden md:block">
        Scroll
      </div> */}
    </section>
  );
}
